import { format } from 'date-fns';
import * as ordersStore from '@/shared/ordersStore';
import { useCallback, useMemo, useState } from 'react';
import { ClientProps } from '@/types';
import { OrderItemsProps } from '../orderStatusPage/useOrderStatusPage';
import { useFetchAxios, useGlobals, useNetworkStatusContext, useUserContext } from '@/hooks';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

interface HookProps {
  order?: OrderItemsProps;
  client?: ClientProps;
  closeModal?: () => void;
  companyData?: {
    clientName: string;
    clientRefNumber: string;
    clientCnpj: string;
    clientUnilever: boolean;
  };
}

function useOrderStatusModal(props = {} as HookProps) {
  const { order, client, companyData, closeModal } = props;

  const { version } = useGlobals();
  const { axiosCtx } = useFetchAxios();
  const { customer } = useUserContext();
  const { connectivityStatus } = useNetworkStatusContext();

  const [loading, setLoading] = useState(false);

  const orderPayload = useMemo(() => {
    if (order) {
      const items = order?.actionsPending?.map((item) => ({
        cod_segmento: item?.cod_segmento,
        sku: item?.sku,
        product: item?.product,
        quantity: item?.quantity,
        quantity_multiplo: item?.quantityMultiplo,
        percentual_flex: item?.percentualFlex || '0',
        integration_price: item?.integrationPrice,
        integration_price_flex: item?.integrationPriceFlex || 0,
        price: item?.price,
        unit_price: item?.unitPrice,
        unit_price_per_item: item?.unitPricePerItem,
        st_price: item?.stPrice,
        st_price_packaging: item?.stPricePackaging,
        packaging: item?.packaging,
        reason: '',
        status: '',

        unilever: item?.UNILEVER,
        price_flex: item?.price_flex,
        st_price_flex: item?.st_price_flex,
        unit_price_flex: item?.unit_price_flex,
        unit_price_per_item_flex: item?.unit_price_per_item_flex,
      }));

      return {
        version,
        origin_function: 'reprocess',
        current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        company: {
          Cod_rca: customer?.codPessoa?.toString(),
          cnpj: companyData?.clientCnpj,
          name: companyData?.clientName,
          id: companyData?.clientRefNumber,
          unilever: companyData?.clientUnilever,
        },
        orders: {
          actions_pending: items,
          order_date: order?.orderDate,
          order_number: order?.orderNumber,
          order_number_magento: order?.orderNumberMagento,
          cod_loja: Number(client?.COD_LOJA || 0),
          erp_order_number: order?.erpNumber,
          origin: 'BateForte_web_mobile_rca',
          items: items?.length,
          total: order?.total,
          payment_terms: order?.paymentTerms,
          status: '',
          reason: '',
        },
      };
    }
  }, [order, client?.COD_LOJA, version, customer?.codPessoa, companyData?.clientCnpj, companyData?.clientName, companyData?.clientRefNumber, companyData?.clientUnilever]);

  const handleReprocessOrder = useCallback(async () => {
    if (!orderPayload) {
      console.log('Order payload is not ready');
      return;
    }

    if (connectivityStatus === 'online') {
      try {
        setLoading(true);

        // BUG-001: legacy code popped from offline_orders and pushed to
        // synched_orders directly in localStorage, clobbering the ordersStore
        // projection. We now drive the store — markAcked moves the record to
        // `acked` (the new-store equivalent of OLD's "processando"), and the
        // projection rewrites both legacy keys atomically.
        const orderNumber = orderPayload?.orders?.order_number;
        if (orderNumber) {
          try {
            await ordersStore.markAcked(orderNumber);
          } catch {
            // mark* on missing record is a no-op; any other error must not
            // block the actual reprocess POST below.
          }
        }

        const ok = await axiosCtx
          .post('/integra-pedido', orderPayload)
          .then((res) => Boolean(res?.data?.message))
          .catch((error) => {
            console.error('Falha ao reenviar pedido:', error);
            return false;
          });

        if (ok) {
          const skus = orderPayload?.orders?.actions_pending?.map((i) => Number(i.sku)) || [];
          await new Promise((resolve) => setTimeout(() => resolve(true), 1000));
          closeModal && closeModal();
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      } finally {
        // unregisterServiceWorker({ preserveLocalStorage: true });
      }
    }
  }, [orderPayload, connectivityStatus, axiosCtx, closeModal]);

  return {
    loading,
    handleReprocessOrder,
  };
}

export default useOrderStatusModal;
